import { DocusignConfig, SettingCardType } from '@monetize/types/app';
import { ReactNode, useEffect, useState } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doGetAdobeAcrobatSignOAuthUrl,
  useGetConfigurationEsign,
} from '../../../../api/settingsService';
import {
  MBox,
  MDivider,
  MGrid,
  MGridItem,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MText,
} from '../../../../components/Monetize';
import { SettingCardEdit } from '../../../../components/Settings/SettingCardEdit';
import { SettingCardEmpty } from '../../../../components/Settings/SettingCardEmpty';
import { useDocumentHead } from '../../../../services/documentHead';
import AdobeAcrobatSignConnection from './components/AdobeAcrobatSignConnection';
import DocusignConnection from './components/DocusignConnection';
import DocusignForm from './components/DocusignForm';

const Esign = () => {
  const { setDocTitle } = useDocumentHead();

  useEffect(() => {
    setDocTitle('Settings', 'eSign');
  }, [setDocTitle]);

  const [editProvider, setEditProvider] = useState<'DOCUSIGN' | 'ADOBE' | null>(
    null,
  );

  const { data: esign, isLoading } = useGetConfigurationEsign({
    refetchOnWindowFocus: false,
  });

  const initiateAdobeAuth = async () => {
    try {
      const { url } = await doGetAdobeAcrobatSignOAuthUrl();
      window.location.href = url;
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const docusignDescription = (
    <MText mb="2">
      Connect your Docusign to send out quotes to be electronically signed.
    </MText>
  );

  const adobeDescription = (
    <MText mb="2">
      Connect your Adobe Acrobat Sign to send out quotes to be electronically
      signed.
    </MText>
  );

  const providers: {
    key: 'DOCUSIGN' | 'ADOBE';
    type: SettingCardType;
    description: ReactNode;
    isLoading?: boolean;
    onClick: (() => void) | (() => Promise<void>);
    renderForm: () => ReactNode;
  }[] = [
    {
      key: 'DOCUSIGN',
      type: 'Docusign',
      description: docusignDescription,
      onClick: () => setEditProvider('DOCUSIGN'),
      renderForm: () => (
        <DocusignForm
          esign={esign as DocusignConfig}
          onClose={() => setEditProvider(null)}
        />
      ),
    },
    {
      key: 'ADOBE',
      type: 'Adobe Acrobat Sign',
      description: adobeDescription,
      onClick: () => initiateAdobeAuth(),
      renderForm: () => <></>,
    },
  ];

  const getEditProvider = () => {
    return providers.find(({ key }) => key === editProvider);
  };

  const inactiveProviders = providers.filter(({ key }) => {
    if (esign?.provider === 'DOCUSIGN') {
      return key !== 'DOCUSIGN';
    }
    if (esign?.provider === 'ADOBE_ACROBAT_SIGN') {
      return key !== 'ADOBE';
    }
    return true;
  });

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Electronic Signature (eSign)" />
      {isLoading ? (
        <MPageLoader />
      ) : (
        <MBox maxW="600px">
          {!esign && !editProvider && (
            <MText color="tGray.darkPurple">
              Add eSign integrations here to allow you to send out quotes to be
              electronically signed
            </MText>
          )}

          {editProvider && getEditProvider() && (
            <>
              {getEditProvider()?.description}
              <SettingCardEdit
                type={getEditProvider()?.type as SettingCardType}
              >
                {getEditProvider()?.renderForm()}
              </SettingCardEdit>
            </>
          )}

          {esign && (
            <>
              {esign.provider === 'DOCUSIGN' && (
                <DocusignConnection connection={esign} />
              )}
              {esign.provider === 'ADOBE_ACROBAT_SIGN' && (
                <AdobeAcrobatSignConnection connection={esign} />
              )}
              {inactiveProviders.length > 0 && (
                <>
                  <MDivider my={6} />
                  <MBox mt={4}>
                    <MText fontWeight="600" fontSize="md">
                      Other Providers
                    </MText>
                    <MText>
                      In order to connect to another provider, you must disable
                      your current connection.
                    </MText>
                  </MBox>
                </>
              )}
            </>
          )}

          {!editProvider && inactiveProviders.length > 0 && (
            <MGrid templateColumns="repeat(12, 1fr)" gap={10} mt={6}>
              {inactiveProviders.map((provider) => (
                <MGridItem key={provider.key} colSpan={6}>
                  <SettingCardEmpty
                    isDisabled={!!esign}
                    type={provider.type}
                    isLoading={provider.isLoading}
                    onClick={provider.onClick}
                  />
                </MGridItem>
              ))}
            </MGrid>
          )}
        </MBox>
      )}
    </MPageContainer>
  );
};

export default Esign;
